import { useState, useEffect, useRef, useMemo, Children } from 'react';
import {
  Box,
  Container,
  useTheme,
  useMediaQuery,
  Drawer,
  Dialog,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Popover,
  Chip,
  ListItemButton,
  Button,
  Card as MuiCard,
  CardContent,
  Stack,
  Avatar as MuiAvatar,
  SvgIcon,
  LinearProgress,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TextField,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { MuiTelInput, matchIsValidTel } from 'mui-tel-input';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome,
  faUsers,
  faClock,
  faPlane,
  faBriefcase,
  faChartLine,
  faWallet,
  faFileAlt,
  faChartBar,
  faCog,
  faBell,
  faSearch,
  faChevronRight,
  faRobot,
  faPaperPlane,
  faTimes,
  faPlus,
  faCheck,
  faBuilding,
  faAward,
  faSignOutAlt,
  faBook,
  faShieldAlt,
  faSparkles,
  faArrowRight,
  faArrowLeft,
  faDownload,
  faUser,
  faStar,
  faMapPin,
  faEnvelope,
  faPhone,
  faPencil,
  faAlertTriangle,
  faHashtag,
  faCalendar,
  faGraduationCap,
  faUserPlus,
  faComment,
  faBars,
} from '@fortawesome/free-solid-svg-icons';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import {
  Wallet,
  Users,
  ShieldCheck,
  Plus,
  Check,
  ArrowLeft,
  ArrowRight,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  Building2,
  GraduationCap,
  Clock,
  X,
  CalendarOff,
  CircleUser,
  Sparkles,
  TrendingUp,
  TrendingDown,
  LayoutDashboard,
  FileText,
  BarChart3,
  Home,
  BookOpen,
  UserPlus,
  AlertTriangle,
  Settings,
  Search,
  Bell,
  Send,
  Hash,
  Calendar,
  Pencil,
  CornerDownLeft,
  ChevronRight,
  MessageSquare,
  LogOut,
  Bot,
  Download,
  Star,
  PanelLeft,
  UserCog,
  SlidersHorizontal,
  Award,
  Menu as MenuIcon,
} from 'lucide-react';
import { api } from './api';
import EmployeesDataGrid from './EmployeesDataGrid';
import AppDataGrid, {
  DataGridCard,
  DataGridPage,
  PersonCell,
  TextCell,
  GridStatusPill,
  useAttendanceColumns,
  useHistoryColumns,
} from './AppDataGrid';
import { useAuth } from './auth';
import { useAppSettings } from './SettingsContext';
import { buildPath, parsePath } from './routing';
import {
  StatusPill,
  UserAvatar,
  Header,
  SectionHeader,
  GridLayout,
  EmptyState,
} from './components';
import { FONT, AG, PIE_COLORS, STATUS_COLOR, statusTone } from './theme/tokens';
import { initials, empEmail, tenure, fmtPKR, fmtM, greeting } from './lib/format';
import { STAGES, JOB_STAGES, jobTone, jobName } from './lib/ats';
import { printPayDoc, docRow, printReport } from './lib/print';
import { OKR_RATINGS, RATING_NUM, ratingTone, okrStatusTone, okrTabBtn, okrLbl, weightedProgress, avgRatingLabel, okrsByEmployee, CYCLE_TYPES, cycleOptionsFor } from './lib/okr';
import { Pill, Avatar, AccountButton, Card, DashboardWidget, WidgetGrid, KPI, CardSectionHeader, ChartCard, SectionTitle, ChartTip, CenterSpinner, PrimaryBtn, OutlineBtn, PageScroll, PTh, PTd, PerfTable, DrawerFormRow, DrawerSection, DrawerTextField, DRAWER_TABLE_SX } from './ui';
import { DocsAdmin, TaxSlabsDialog } from './adminViews';
import ApprovalsInbox from './ApprovalsInbox';
import Letters from './Letters';
import Requests from './Requests';
import MyProfile from './MyProfile';
import TeamAnalytics from './TeamAnalytics';
import MyLearning from './MyLearning';
import MySkills from './MySkills';
import Feedback from './Feedback';
import Recognition from './Recognition';
import Awards from './Awards';
import Pulse from './Pulse';
import Cases from './Cases';
import Announcements from './Announcements';
import Safety from './Safety';
import Benefits from './Benefits';
import Disciplinary from './Disciplinary';
import Loans from './Loans';
import SalaryStructure from './SalaryStructure';
import SettlementCard from './SettlementCard';
import OvertimeCard from './OvertimeCard';
import AdjustmentsCard from './AdjustmentsCard';

function EmployeeViews({
  view,
  user,
  leaves,
  balances,
  submitLeave,
  cancelLeave,
  showToast,
}) {
  if (view === 'Dashboard')
    return (
      <PageScroll>
        <EmpDashboard user={user} balances={balances} leaves={leaves} />
      </PageScroll>
    );
  if (view === 'Attendance') return <EmpAttendance showToast={showToast} />;
  if (view === 'Leave')
    return (
      <PageScroll>
        <EmpLeave
          leaves={leaves}
          balances={balances}
          submitLeave={submitLeave}
          cancelLeave={cancelLeave}
        />
      </PageScroll>
    );
  if (view === 'Payroll') return <EmpPayroll />;
  if (view === 'My Benefits')
    return (
      <PageScroll>
        <Benefits showToast={showToast} />
      </PageScroll>
    );
  if (view === 'My Loans')
    return (
      <PageScroll>
        <Loans showToast={showToast} />
      </PageScroll>
    );
  if (view === 'My Record')
    return (
      <PageScroll>
        <Disciplinary showToast={showToast} />
      </PageScroll>
    );
  if (view === 'Documents')
    return (
      <PageScroll>
        <EmpDocuments />
      </PageScroll>
    );
  if (view === 'Performance')
    return (
      <PageScroll>
        <EmpPerformance showToast={showToast} />
      </PageScroll>
    );
  if (view === 'Approvals')
    return (
      <PageScroll>
        <ApprovalsInbox showToast={showToast} />
      </PageScroll>
    );
  if (view === 'Letters')
    return (
      <PageScroll>
        <Letters showToast={showToast} />
      </PageScroll>
    );
  if (view === 'Requests')
    return (
      <PageScroll>
        <Requests showToast={showToast} />
      </PageScroll>
    );
  if (view === 'My Profile')
    return (
      <PageScroll>
        <MyProfile showToast={showToast} />
      </PageScroll>
    );
  if (view === 'My Learning')
    return (
      <PageScroll>
        <MyLearning showToast={showToast} />
      </PageScroll>
    );
  if (view === 'My Skills')
    return (
      <PageScroll>
        <MySkills />
      </PageScroll>
    );
  if (view === 'Feedback')
    return (
      <PageScroll>
        <Feedback showToast={showToast} />
      </PageScroll>
    );
  if (view === 'Recognition')
    return (
      <PageScroll>
        <Recognition showToast={showToast} />
      </PageScroll>
    );
  if (view === 'Awards')
    return (
      <PageScroll>
        <Awards showToast={showToast} />
      </PageScroll>
    );
  if (view === 'Pulse')
    return (
      <PageScroll>
        <Pulse showToast={showToast} />
      </PageScroll>
    );
  if (view === 'Employee Relations')
    return (
      <PageScroll>
        <Cases showToast={showToast} />
      </PageScroll>
    );
  if (view === 'Announcements')
    return (
      <PageScroll>
        <Announcements showToast={showToast} />
      </PageScroll>
    );
  if (view === 'Health & Safety')
    return (
      <PageScroll>
        <Safety showToast={showToast} />
      </PageScroll>
    );
  if (view === 'Team Analytics')
    return (
      <PageScroll>
        <TeamAnalytics />
      </PageScroll>
    );
  if (view === "Team's Attendance") return <TeamAttendance />;
  if (view === "Team's Leaves")
    return (
      <PageScroll>
        <TeamLeave showToast={showToast} />
      </PageScroll>
    );
  if (view === "Team's Performance")
    return (
      <PageScroll>
        <TeamPerformance showToast={showToast} />
      </PageScroll>
    );
  if (view === 'Organization Docs')
    return (
      <PageScroll>
        <DocsAdmin kind="Form" readOnly />
      </PageScroll>
    );
  if (view === 'Policies')
    return (
      <PageScroll>
        <EmpPolicies showToast={showToast} />
      </PageScroll>
    );
  return null;
}

// Manager view: today's attendance for the manager's direct reports.
function TeamAttendance() {
  const [data, setData] = useState(null);
  const attendanceColumns = useAttendanceColumns();
  useEffect(() => {
    let a = true;
    api
      .attendanceTeam()
      .then((d) => a && setData(d))
      .catch(() => {});
    return () => {
      a = false;
    };
  }, []);
  if (!data) return <CenterSpinner />;
  const rows = data.rows || [];
  const s = data.summary || { present: 0, late: 0, absent: 0 };
  return (
    <div className="page-scroll fade-up">
      <SectionTitle
        title="Team's Attendance"
        sub={`${rows.length} team member${rows.length === 1 ? '' : 's'} · today`}
      />
      <WidgetGrid>
        <KPI
          label="Present"
          value={String(s.present)}
          icon={Check}
          iconTone="success"
          delay={0}
        />
        <KPI
          label="Late"
          value={String(s.late)}
          icon={Clock}
          iconTone="warning"
          delay={60}
        />
        <KPI
          label="Absent"
          value={String(s.absent)}
          icon={X}
          iconTone="error"
          delay={120}
        />
      </WidgetGrid>
      {rows.length === 0 ? (
        <Card>
          <EmptyState
            icon={Users}
            title="No team members"
            desc="No one currently reports to you."
          />
        </Card>
      ) : (
        <DataGridCard fillHeight={false} title="Today — my team">
          <AppDataGrid
            rows={rows}
            columns={attendanceColumns}
            autoHeight
            fillHeight={false}
            hideFooter={rows.length <= 10}
          />
        </DataGridCard>
      )}
    </div>
  );
}

// Manager view: approve/reject leave requests from the manager's direct reports.
function TeamLeave({ showToast }) {
  const [rows, setRows] = useState(null);
  const [tab, setTab] = useState('Pending Manager');
  const [busy, setBusy] = useState(null);
  useEffect(() => {
    let a = true;
    api
      .leavesTeam()
      .then((d) => a && setRows(d))
      .catch(() => {
        if (a) setRows([]);
      });
    return () => {
      a = false;
    };
  }, []);

  const decide = async (id, decision) => {
    setBusy(id);
    try {
      const updated = await api.managerDecideLeave(id, decision);
      setRows((prev) => prev.map((l) => (l.id === id ? updated : l)));
      showToast(
        decision === 'Approved'
          ? 'Approved — forwarded to HR'
          : 'Request rejected',
      );
    } catch (e) {
      showToast(e.message);
    } finally {
      setBusy(null);
    }
  };

  if (!rows) return <CenterSpinner />;
  const tabs = ['Pending Manager', 'Pending HR', 'Approved', 'Rejected'];
  const label = {
    'Pending Manager': 'Awaiting me',
    'Pending HR': 'With HR',
    Approved: 'Approved',
    Rejected: 'Rejected',
  };
  const list = rows.filter((l) => l.status === tab);
  return (
    <div className="space-y-4 fade-up">
      <SectionTitle
        title="Team's Leaves"
        sub="Review and approve your team's leave requests"
      />
      <div className="flex gap-2 flex-wrap">
        {tabs.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className="navlift"
            style={{
              padding: '8px 16px',
              borderRadius: 12,
              fontSize: 14,
              fontWeight: 500,
              background: tab === t ? AG.primary : '#fff',
              color: tab === t ? '#fff' : AG.n600,
              border: `1px solid ${tab === t ? AG.primary : AG.n200}`,
            }}
          >
            {label[t]}{' '}
            <span style={{ opacity: 0.7 }}>
              ({rows.filter((l) => l.status === t).length})
            </span>
          </button>
        ))}
      </div>
      {list.length === 0 ? (
        <Card>
          <EmptyState
            icon={CalendarOff}
            title={`Nothing here`}
            desc={
              tab === 'Pending Manager'
                ? 'No requests waiting for your approval.'
                : `No requests in "${label[tab]}".`
            }
          />
        </Card>
      ) : (
        <div className="space-y-3">
          {list.map((l) => (
            <Card key={l.id} style={{ padding: 16 }}>
              <div className="flex items-center gap-4 flex-wrap">
                <Avatar name={l.emp} />
                <div className="flex-1" style={{ minWidth: 200 }}>
                  <div className="flex items-center gap-2">
                    <p style={{ fontWeight: 600, color: AG.text }}>{l.emp}</p>
                    <Pill tone="neutral">{l.dept}</Pill>
                  </div>
                  <p style={{ color: AG.textSec, marginTop: 2, fontSize: 14 }}>
                    {l.type} · {l.from} → {l.to} ·{' '}
                    <span style={{ fontWeight: 500, color: AG.n700 }}>
                      {l.days} day{l.days > 1 ? 's' : ''}
                    </span>
                  </p>
                  <p style={{ color: AG.n400, marginTop: 4, fontSize: 12 }}>
                    "{l.reason}"
                  </p>
                </div>
                {l.status === 'Pending Manager' ? (
                  <div className="flex gap-2">
                    <button
                      disabled={busy === l.id}
                      onClick={() => decide(l.id, 'Rejected')}
                      className="ag-btn flex items-center gap-1"
                      style={{
                        color: AG.errorDark,
                        background: 'rgba(240,68,56,.12)',
                        border: 'none',
                      }}
                    >
                      <X size={15} />
                      Reject
                    </button>
                    <button
                      disabled={busy === l.id}
                      onClick={() => decide(l.id, 'Approved')}
                      className="ag-btn ag-btn-primary flex items-center gap-1"
                    >
                      <Check size={15} />
                      Approve
                    </button>
                  </div>
                ) : (
                  <Pill tone={statusTone(l.status)} dot>
                    {l.status}
                  </Pill>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function EmpDashboard({ user, balances, leaves }) {
  const mine = leaves;
  // Maternity / Paternity are gender-specific — only show the one that applies.
  const g = String(user?.gender || '').toLowerCase();
  const visBalances = balances.filter(
    (b) =>
      !(b.type === 'Maternity Leave' && g === 'male') &&
      !(b.type === 'Paternity Leave' && g === 'female'),
  );
  const annual = balances.find((b) => b.type === 'Annual Leave') || {
    entitled: 0,
    used: 0,
  };
  const dateStr = new Date().toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  return (
    <div className="space-y-6 fade-up">
      <Card
        style={{ padding: 24, background: AG.primaryDarkest, border: 'none' }}
      >
        <div className="flex items-center gap-4">
          <Avatar name={user?.name} size="lg" onDark />
          <div>
            <p
              style={{
                color: '#fff',
                fontSize: 22,
                fontWeight: 700,
                letterSpacing: '-0.01em',
              }}
            >
              {greeting()}, {user?.name?.split(' ')[0]} 👋
            </p>
            <p style={{ color: AG.primaryLight, marginTop: 2, fontSize: 14 }}>
              {user?.role} · {user?.dept} · {dateStr}
            </p>
          </div>
        </div>
      </Card>
      <WidgetGrid>
        <KPI
          label="Annual leave left"
          value={`${annual.entitled - annual.used} days`}
          icon={CalendarOff}
          iconTone="primary"
          delay={0}
        />
        <KPI
          label="Pending requests"
          value={String(
            mine.filter((l) => String(l.status).startsWith('Pending')).length,
          )}
          icon={FileText}
          iconTone="warning"
          delay={60}
        />
        <KPI
          label="Approved leaves"
          value={String(mine.filter((l) => l.status === 'Approved').length)}
          icon={Check}
          iconTone="success"
          delay={120}
        />
        <KPI
          label="Total requests"
          value={String(mine.length)}
          icon={TrendingUp}
          iconTone="purple"
          delay={180}
        />
      </WidgetGrid>
      <Grid container spacing={2}>
        <Grid xs={12} lg={8}>
          <ChartCard title="Recent leave activity" sub="Your latest requests">
            {mine.length === 0 ? (
              <EmptyState
                icon={CalendarOff}
                title="No requests yet"
                desc="Apply for leave from Leave."
              />
            ) : (
              <Stack spacing={1}>
                {mine.slice(0, 4).map((l) => (
                  <Stack
                    key={l.id}
                    direction="row"
                    alignItems="center"
                    gap={1.5}
                    sx={{ p: 1.5, bgcolor: AG.n50, borderRadius: 1.5 }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1,
                        bgcolor: 'background.paper',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: AG.n400,
                        flexShrink: 0,
                      }}
                    >
                      <CalendarOff size={15} />
                    </Box>
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      sx={{ flex: 1, minWidth: 0 }}
                    >
                      {l.type} · {l.from} → {l.to}
                    </Typography>
                    <Pill tone={statusTone(l.status)} dot>
                      {l.status}
                    </Pill>
                  </Stack>
                ))}
              </Stack>
            )}
          </ChartCard>
        </Grid>
        <Grid xs={12} lg={4}>
          <ChartCard title="Leave balances">
            <Stack spacing={1.5}>
              {visBalances.map((b) => (
                <Box key={b.type}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    sx={{ mb: 0.5 }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      color="text.secondary"
                    >
                      {b.type}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {b.entitled - b.used}/{b.entitled}
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={b.entitled ? (b.used / b.entitled) * 100 : 0}
                    sx={{
                      height: 8,
                      borderRadius: 1,
                      bgcolor: AG.n100,
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 1,
                        bgcolor: AG.primary,
                      },
                    }}
                  />
                </Box>
              ))}
            </Stack>
          </ChartCard>
        </Grid>
      </Grid>
    </div>
  );
}

function EmpAttendance({ showToast }) {
  const [data, setData] = useState(null);
  const historyColumns = useHistoryColumns();
  const load = () =>
    api
      .attendanceMe()
      .then(setData)
      .catch(() => {});
  useEffect(() => {
    load();
  }, []);
  const todayRow = data?.today;
  const clockedIn = !!(todayRow?.clockIn && !todayRow?.clockOut);
  const autoSource = !!(data?.source && data.source !== 'Manual'); // Device/Login → no manual clock
  const hist = data?.history || [];
  const histRows = useMemo(() => hist.map((h, i) => ({ id: i, ...h })), [hist]);
  const toggle = async () => {
    try {
      if (!clockedIn && !todayRow?.clockOut) {
        const r = await api.clock('in');
        showToast(
          r.status === 'Late'
            ? `⚠️ You're late — clocked in at ${r.clockIn} (after 10:35)`
            : `Clocked in at ${r.clockIn}`,
        );
      } else if (clockedIn) {
        await api.clock('out');
        showToast('Clocked out — have a great day!');
      }
      load();
    } catch (e) {
      showToast(e.message);
    }
  };
  const dateStr = new Date().toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const status = todayRow?.clockOut
    ? `Done · ${todayRow.clockIn}–${todayRow.clockOut}`
    : clockedIn
      ? `Clocked in · ${todayRow.clockIn}`
      : 'Not clocked in';
  return (
    <DataGridPage>
      <Card className="page-shrink" style={{ padding: 24 }}>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <p style={{ color: AG.textSec, fontWeight: 500, fontSize: 14 }}>
              Today · {dateStr}
            </p>
            <p
              style={{
                fontSize: 30,
                fontWeight: 700,
                color: AG.text,
                marginTop: 4,
                letterSpacing: '-0.02em',
              }}
            >
              {status}
            </p>
          </div>
          <button
            onClick={toggle}
            disabled={!!todayRow?.clockOut || autoSource}
            className="ag-btn"
            style={{
              padding: '12px 24px',
              fontSize: 15,
              color: '#fff',
              background: clockedIn ? AG.error : AG.primary,
              border: 'none',
              opacity: todayRow?.clockOut || autoSource ? 0.5 : 1,
            }}
          >
            {clockedIn ? 'Clock out' : 'Clock in'}
          </button>
        </div>
        <div
          className="flex items-center gap-2"
          style={{ marginTop: 16, color: AG.n400, fontSize: 12 }}
        >
          <MapPin size={13} />
          {autoSource ? `Captured automatically via ${data.source} — manual clock-in is disabled` : 'Augment HQ · verified location'}
        </div>
      </Card>
      <WidgetGrid>
        <KPI
          label="Recent records"
          value={String(hist.length)}
          icon={Check}
          iconTone="success"
          delay={0}
        />
        <KPI
          label="Present"
          value={String(hist.filter((h) => h.status === 'Present').length)}
          icon={Clock}
          iconTone="info"
          delay={60}
        />
        <KPI
          label="Late"
          value={String(hist.filter((h) => h.status === 'Late').length)}
          icon={Clock}
          iconTone="warning"
          delay={120}
        />
      </WidgetGrid>
      <DataGridCard title="Recent history">
        <AppDataGrid
          rows={histRows}
          columns={historyColumns}
          hideFooter={histRows.length <= 10}
          cursor="default"
        />
      </DataGridCard>
    </DataGridPage>
  );
}

function EmpLeave({ leaves, balances, submitLeave, cancelLeave }) {
  const { user } = useAuth();
  const todayStr = new Date().toISOString().slice(0, 10);
  const canCancel = (l) => ['Pending Manager', 'Pending HR'].includes(l.status) || (l.status === 'Approved' && l.to >= todayStr);
  // Maternity / Paternity are gender-specific — only show the one that applies.
  const g = String(user?.gender || '').toLowerCase();
  const visBalances = balances.filter(
    (b) =>
      !(b.type === 'Maternity Leave' && g === 'male') &&
      !(b.type === 'Paternity Leave' && g === 'female'),
  );
  const [form, setForm] = useState({ type: 'Annual Leave', from: '', to: '', reason: '' });
  const mine = leaves;

  // Leave types come from configuration (active + eligible for this employee),
  // not a hardcoded list — falls back to the balance types until they load.
  const [types, setTypes] = useState(null);
  useEffect(() => {
    api.availableLeaveTypes().then(setTypes).catch(() => setTypes([]));
  }, []);
  const typeNames = types && types.length ? types.map((t) => t.name) : visBalances.map((b) => b.type);
  // Balance cards mirror the configured types the employee is eligible for.
  const cardBalances = types && types.length ? balances.filter((b) => typeNames.includes(b.type)) : visBalances;
  useEffect(() => {
    if (typeNames.length && !typeNames.includes(form.type)) setForm((f) => ({ ...f, type: typeNames[0] }));
  }, [types]);

  // Chargeable days are computed server-side (weekends + the entity's holidays
  // excluded); fall back to a naive inclusive count until the preview returns.
  const daysBetween = (a, b) => {
    if (!a || !b) return 0;
    const d = (new Date(b) - new Date(a)) / 86400000 + 1;
    return d > 0 ? d : 0;
  };
  const [chargeable, setChargeable] = useState(null);
  useEffect(() => {
    if (!form.from || !form.to) { setChargeable(null); return; }
    let active = true;
    api.workingDays(form.from, form.to).then((r) => { if (active) setChargeable(r.days); }).catch(() => {});
    return () => { active = false; };
  }, [form.from, form.to]);
  const days = chargeable != null ? chargeable : daysBetween(form.from, form.to);
  const selBal = visBalances.find((b) => b.type === form.type) || {
    entitled: 0,
    used: 0,
  };
  const pendingDays = mine
    .filter(
      (l) => l.type === form.type && String(l.status).startsWith('Pending'),
    )
    .reduce((s, l) => s + l.days, 0);
  const available = Math.max(0, selBal.entitled - selBal.used - pendingDays);
  const overLimit = days > available;
  const submit = () => {
    if (!form.from || !form.to || !form.reason || overLimit) return;
    submitLeave({
      type: form.type,
      from: form.from,
      to: form.to,
      days,
      reason: form.reason,
    });
    setForm({ type: 'Annual Leave', from: '', to: '', reason: '' });
  };
  return (
    <div className="space-y-4 fade-up">
      <SectionTitle
        title="Leave"
        sub="Apply for leave and track your requests"
      />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {cardBalances.map((b) => (
          <Card key={b.type} style={{ padding: 16 }}>
            <p style={{ fontSize: 12, fontWeight: 500, color: AG.textSec }}>
              {b.type}
            </p>
            <p
              style={{
                fontSize: 22,
                fontWeight: 700,
                color: AG.text,
                marginTop: 4,
              }}
            >
              {b.entitled - b.used}
              <span style={{ fontSize: 13, color: AG.n400, fontWeight: 500 }}>
                {' '}
                / {b.entitled}
              </span>
            </p>
            <div
              style={{
                marginTop: 8,
                height: 6,
                background: AG.n100,
                borderRadius: 4,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  borderRadius: 4,
                  width: b.entitled ? `${(b.used / b.entitled) * 100}%` : '0%',
                  background: AG.primary,
                }}
              />
            </div>
          </Card>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-1" style={{ padding: 24 }}>
          <h3 style={{ fontWeight: 600, color: AG.text, marginBottom: 16 }}>
            Apply for leave
          </h3>
          <div className="space-y-3">
            <div>
              <label
                style={{ fontSize: 12, fontWeight: 500, color: AG.textSec }}
              >
                Leave type
              </label>
              <select
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                className="ag-input"
                style={{ marginTop: 4 }}
              >
                {typeNames.map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </select>
              <p style={{ fontSize: 12, color: AG.textSec, marginTop: 4 }}>
                <b style={{ color: available > 0 ? AG.successDark : AG.error }}>
                  {available}
                </b>{' '}
                day{available === 1 ? '' : 's'} available
                {pendingDays ? ` · ${pendingDays} pending` : ''}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label
                  style={{ fontSize: 12, fontWeight: 500, color: AG.textSec }}
                >
                  From
                </label>
                <input
                  type="date"
                  value={form.from}
                  onChange={(e) => setForm({ ...form, from: e.target.value })}
                  className="ag-input"
                  style={{ marginTop: 4 }}
                />
              </div>
              <div>
                <label
                  style={{ fontSize: 12, fontWeight: 500, color: AG.textSec }}
                >
                  To
                </label>
                <input
                  type="date"
                  value={form.to}
                  onChange={(e) => setForm({ ...form, to: e.target.value })}
                  className="ag-input"
                  style={{ marginTop: 4 }}
                />
              </div>
            </div>
            {days > 0 && !overLimit && (
              <p
                style={{
                  fontSize: 12,
                  color: AG.primaryDark,
                  background: AG.primaryLightest,
                  borderRadius: 8,
                  padding: '8px 12px',
                  fontWeight: 500,
                }}
              >
                {days} chargeable day{days > 1 ? 's' : ''}
                {chargeable != null ? ' (weekends & holidays excluded)' : ''}
              </p>
            )}
            {overLimit && (
              <p
                style={{
                  fontSize: 12,
                  color: AG.errorDark,
                  background: 'rgba(240,68,56,.12)',
                  borderRadius: 8,
                  padding: '8px 12px',
                  fontWeight: 500,
                }}
              >
                You requested {days} day{days > 1 ? 's' : ''} but only{' '}
                {available} available for {form.type}.
              </p>
            )}
            <div>
              <label
                style={{ fontSize: 12, fontWeight: 500, color: AG.textSec }}
              >
                Reason
              </label>
              <textarea
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                rows={2}
                placeholder="Brief reason..."
                className="ag-input"
                style={{ marginTop: 4, resize: 'none' }}
              />
            </div>
            <button
              onClick={submit}
              disabled={!form.from || !form.to || !form.reason || overLimit}
              className="ag-btn ag-btn-primary w-full"
            >
              Submit application
            </button>
            <p style={{ fontSize: 11, color: AG.n400, textAlign: 'center' }}>
              Goes to your manager, then HR for final approval
            </p>
          </div>
        </Card>
        <Card className="lg:col-span-2" style={{ padding: 24 }}>
          <h3 style={{ fontWeight: 600, color: AG.text, marginBottom: 16 }}>
            Applications
          </h3>
          {mine.length === 0 ? (
            <EmptyState
              icon={CalendarOff}
              title="No applications yet"
              desc="Submit your first leave request using the form on the left."
            />
          ) : (
            <div className="space-y-2">
              {mine.map((l) => (
                <div
                  key={l.id}
                  className="flex items-center gap-3"
                  style={{ padding: 12, background: AG.n50, borderRadius: 12 }}
                >
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 8,
                      background: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: AG.n500,
                    }}
                  >
                    <CalendarOff size={16} />
                  </div>
                  <div className="flex-1">
                    <p
                      style={{ fontSize: 14, fontWeight: 500, color: AG.text }}
                    >
                      {l.type}
                    </p>
                    <p style={{ fontSize: 12, color: AG.n400 }}>
                      {l.from} → {l.to} · {l.days} day{l.days > 1 ? 's' : ''}
                    </p>
                  </div>
                  <Pill tone={statusTone(l.status)} dot>
                    {l.status}
                  </Pill>
                  {cancelLeave && canCancel(l) && (
                    <button
                      onClick={() => cancelLeave(l.id)}
                      style={{ background: 'none', border: `1px solid ${AG.n200}`, borderRadius: 8, padding: '4px 10px', fontSize: 12, fontWeight: 600, color: AG.n500, cursor: 'pointer' }}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}


function EmpPayroll() {
  const [data, setData] = useState(null);
  const [slabsOpen, setSlabsOpen] = useState(false);
  const [tab, setTab] = useState('Salary slip');
  useEffect(() => {
    let a = true;
    api
      .payrollMe()
      .then((d) => a && setData(d))
      .catch(() => {});
    return () => { a = false; };
  }, []);

  if (!data) {
    return (
      <div className="page-scroll flex items-center justify-center">
        <CenterSpinner />
      </div>
    );
  }
  const { employee, pay, tax, payslips } = data;

  const empBlock = `
    <h2>Employee</h2>
    <table>
      ${docRow('Name', employee.name)}
      ${docRow('Employee ID', employee.id)}
      ${docRow('Designation', `${employee.role} · ${employee.dept}`)}
      ${docRow('Work email', employee.email || '—')}
    </table>`;

  const printCert = () => {
    api.trackDownload('DOWNLOAD_TAX_CERTIFICATE', `Tax Year ${new Date().getFullYear()}`);
    printPayDoc(
      `Income Tax Certificate — ${employee.name}`,
      `<div class="hdr"><h1>Augment HR</h1><p>Income Tax Deduction Certificate · Tax Year ${new Date().getFullYear()}</p></div>
       ${empBlock}
       <h2>Income tax summary</h2>
       <table>
         ${docRow('Monthly gross salary', fmtPKR(tax.monthlyGross))}
         ${docRow('Annual gross salary', fmtPKR(tax.annualGross))}
         ${docRow('Annual income tax', fmtPKR(tax.annualTax))}
         ${docRow('Monthly income tax', fmtPKR(tax.monthlyTax))}
         <tr class="tot"><td class="k">Effective tax rate</td><td class="v">${tax.effectiveRate}%</td></tr>
       </table>`,
      `This income tax deduction certificate is system-generated by Augment HR for the tax year ${new Date().getFullYear()}.`,
    );
  };

  const printPayslip = async (r) => {
    api.trackDownload('DOWNLOAD_PAYSLIP', r.month);
    let detail = null;
    try { detail = await api.payslipDetail(r.id); } catch { /* fall back to totals */ }
    const lines = detail?.lines || [];
    const earnRows = lines.filter((l) => l.kind === 'Earning').map((l) => docRow(l.label, fmtPKR(l.amount))).join('');
    const dedRows = lines.filter((l) => l.kind === 'Deduction').map((l) => docRow(l.label, `− ${fmtPKR(l.amount)}`)).join('');
    printPayDoc(
      `Payslip — ${r.month}`,
      `<div class="hdr"><h1>Augment HR</h1><p>Salary Payslip · ${r.month}</p></div>
       ${empBlock}
       <h2>Earnings</h2>
       <table>${earnRows || docRow('Basic salary', fmtPKR(r.gross))}${r.overtime ? '' : ''}</table>
       <h2>Deductions</h2>
       <table>${dedRows || docRow('Total deductions', fmtPKR(r.gross + (r.overtime || 0) - r.net))}</table>
       <table><tr class="tot"><td class="k">Net pay</td><td class="v">${fmtPKR(r.net)}</td></tr></table>`,
    );
  };

  const Row = ({ label, value, accent, strong, top }) => (
    <div className="flex items-center justify-between" style={{ fontSize: 14, padding: '9px 0', borderBottom: top ? 'none' : `1px solid ${AG.divider}`, borderTop: top ? `2px solid ${AG.n200}` : 'none', marginTop: top ? 4 : 0 }}>
      <span style={{ color: AG.n500 }}>{label}</span>
      <span style={{ color: accent ? AG.primary : AG.text, fontWeight: strong ? 700 : 500 }}>{value}</span>
    </div>
  );

  const slipCols = [
    { field: 'month', headerName: 'Month', flex: 1, minWidth: 120, renderCell: ({ value }) => <TextCell value={value} fontWeight={500} /> },
    { field: 'gross', headerName: 'Gross', flex: 0.8, minWidth: 110, renderCell: ({ value }) => <TextCell value={fmtPKR(value)} /> },
    { field: 'overtime', headerName: 'Overtime', flex: 0.7, minWidth: 100, renderCell: ({ value }) => <TextCell value={value ? fmtPKR(value) : '—'} /> },
    { field: 'deductions', headerName: 'Deductions', flex: 0.8, minWidth: 120, renderCell: ({ row }) => <TextCell value={fmtPKR(row.gross + (row.overtime || 0) - row.net)} /> },
    { field: 'net', headerName: 'Net pay', flex: 0.8, minWidth: 110, renderCell: ({ value }) => <TextCell value={fmtPKR(value)} fontWeight={600} /> },
    { field: 'status', headerName: 'Status', flex: 0.6, minWidth: 90, renderCell: ({ value }) => <GridStatusPill status={value} tone="success" /> },
    {
      field: 'actions', headerName: '', width: 110, sortable: false,
      renderCell: ({ row }) => (
        <button onClick={() => printPayslip(row)} className="inline-flex items-center gap-1" style={{ color: AG.primary, fontWeight: 500, fontSize: 14 }}>
          <Download size={14} />PDF
        </button>
      ),
    },
  ];

  const tabs = ['Salary slip', 'Income Tax', 'Benefits & Contributions'];

  return (
    <div className="page-scroll fade-up">
      <SectionTitle title="Payroll" sub="Your salary, benefits, deductions and income tax" />

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: AG.primaryLightest, border: `1px solid ${AG.primaryLight}`, color: AG.primaryDark, borderRadius: 12, padding: '12px 16px', fontSize: 13, fontWeight: 500 }}>
        <Check size={16} /> System-generated payslip — does not require any signature or verification.
      </div>

      <div className="tab-strip page-shrink">
        {tabs.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`navlift tab-strip-btn${tab === t ? ' tab-strip-btn--active' : ''}`}
          >
            <span>{t}</span>
          </button>
        ))}
      </div>

      {tab === 'Salary slip' && (
        <>
          <WidgetGrid>
            <KPI label="Gross (monthly)" value={fmtPKR(pay.gross)} icon={Wallet} iconTone="primary" delay={0} />
            <KPI label="Total deductions" value={fmtPKR(pay.deductions)} icon={ArrowRight} iconTone="warning" delay={60} />
            <KPI label="Income tax" value={fmtPKR(pay.tax)} icon={FileText} iconTone="error" delay={120} />
            <KPI label="Net pay" value={fmtPKR(pay.net)} icon={Check} iconTone="success" delay={180} />
          </WidgetGrid>

          <Card style={{ padding: 20 }}>
            <h3 style={{ fontWeight: 600, color: AG.text, marginBottom: 8 }}>Earnings &amp; deductions</h3>
            <Row label="Gross salary" value={fmtPKR(pay.gross)} />
            <Row label="Provident Fund (6.5%)" value={`− ${fmtPKR(pay.pfEmployee)}`} />
            <Row label="Income tax" value={`− ${fmtPKR(pay.tax)}`} />
            <Row label="Net pay" value={fmtPKR(pay.net)} accent strong top />
          </Card>

          <SalaryStructure />

          <AdjustmentsCard />

          <OvertimeCard />

          <SettlementCard />

          <DataGridCard fillHeight={false} title="Payslip history">
            <AppDataGrid rows={payslips} columns={slipCols} autoHeight fillHeight={false} hideFooter={payslips.length <= 10} cursor="default" />
          </DataGridCard>
        </>
      )}

      {tab === 'Income Tax' && (
        <Card style={{ padding: 20 }}>
          <div className="flex items-center justify-between flex-wrap gap-2" style={{ marginBottom: 8 }}>
            <h3 style={{ fontWeight: 600, color: AG.text }}>Income tax</h3>
            <OutlineBtn icon={Download} onClick={printCert}>Tax certificate (PDF)</OutlineBtn>
          </div>
          <Row label="Monthly gross" value={fmtPKR(tax.monthlyGross)} />
          <Row label="Annual gross" value={fmtPKR(tax.annualGross)} />
          <Row label="Annual income tax" value={fmtPKR(tax.annualTax)} />
          <Row label="Monthly income tax" value={fmtPKR(tax.monthlyTax)} />
          <Row label="Effective tax rate" value={`${tax.effectiveRate}%`} accent strong top />
          <div style={{ marginTop: 14 }}>
            <OutlineBtn icon={FileText} onClick={() => setSlabsOpen(true)}>View tax slabs</OutlineBtn>
          </div>
          <TaxSlabsDialog slabs={tax.slabs} open={slabsOpen} onClose={() => setSlabsOpen(false)} />
        </Card>
      )}

      {tab === 'Benefits & Contributions' && (
        <Card style={{ padding: 20 }}>
          <h3 style={{ fontWeight: 600, color: AG.text, marginBottom: 4 }}>Employer contributions (benefits)</h3>
          <p style={{ fontSize: 13, color: AG.n500, marginBottom: 8 }}>Paid by the company on top of your gross salary.</p>
          <Row label="Provident Fund — employer (6.5%)" value={fmtPKR(pay.pfEmployer)} />
          <Row label="EOBI" value={fmtPKR(pay.eobi)} />
          <Row label="OPD / Medical (5%)" value={fmtPKR(pay.opd)} />
          <Row label="Total employer benefit" value={fmtPKR(pay.pfEmployer + pay.eobi + pay.opd)} strong top />
        </Card>
      )}
    </div>
  );
}

function EmpDocuments() {
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [consents, setConsents] = useState([]);
  const [signing, setSigning] = useState(null); // { id, name }
  const todayStr = new Date().toISOString().slice(0, 10);
  const loadConsents = () => api.myConsents().then(setConsents).catch(() => {});
  useEffect(() => {
    let a = true;
    api.myDocuments().then((d) => a && setDocs(d)).catch(() => {});
    loadConsents();
    return () => { a = false; };
  }, []);

  const handleDownload = (doc) => {
    api.downloadMyDocument(doc.id, doc.name).catch(() => {});
  };
  const doSign = async (c) => {
    const name = (signing?.name || '').trim() || user?.name;
    if (!name) return;
    try { await api.signConsent(c.id, name); setSigning(null); loadConsents(); } catch (e) { /* surfaced elsewhere */ }
  };
  const doDecline = async (c) => { try { await api.declineConsent(c.id); loadConsents(); } catch (e) { /* */ } };
  const pendingConsents = consents.filter((c) => c.status === 'Pending');
  const consentTone = (s) => (s === 'Signed' ? AG.successDark : s === 'Declined' ? AG.errorDark : AG.warningDark);
  const fmtSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-4 fade-up">
      <SectionTitle title="My Documents" sub="Your secure document vault" />

      {/* Agreements & consents (NDA / data consent) */}
      {consents.length > 0 && (
        <Card style={{ padding: 18 }}>
          <p style={{ fontWeight: 600, color: AG.text, marginBottom: 12 }}>Agreements &amp; consents</p>
          <div style={{ display: 'grid', gap: 10 }}>
            {consents.map((c) => (
              <div key={c.id} style={{ border: `1px solid ${AG.n200}`, borderRadius: 12, padding: 14 }}>
                <div className="flex items-start justify-between" style={{ gap: 10, flexWrap: 'wrap' }}>
                  <div style={{ minWidth: 0 }}>
                    <p style={{ fontWeight: 600, fontSize: 14, color: AG.text }}>{c.title} <span style={{ fontWeight: 400, color: AG.n400 }}>· {c.kind}</span></p>
                    {c.body && <p style={{ fontSize: 12.5, color: AG.n500, marginTop: 4 }}>{c.body}</p>}
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 700, color: consentTone(c.status) }}>{c.status}{c.signedAt ? ` · ${c.signatureName}` : ''}</span>
                </div>
                {c.status === 'Pending' && (
                  signing?.id === c.id ? (
                    <div className="flex items-center gap-2" style={{ marginTop: 10, flexWrap: 'wrap' }}>
                      <input value={signing.name} onChange={(e) => setSigning({ id: c.id, name: e.target.value })} placeholder="Type your full name to sign" className="ag-input" style={{ flex: 1, minWidth: 180 }} />
                      <PrimaryBtn onClick={() => doSign(c)}>Sign</PrimaryBtn>
                      <OutlineBtn onClick={() => setSigning(null)}>Cancel</OutlineBtn>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2" style={{ marginTop: 10 }}>
                      <PrimaryBtn onClick={() => setSigning({ id: c.id, name: user?.name || '' })}>Review &amp; sign</PrimaryBtn>
                      <OutlineBtn onClick={() => doDecline(c)}>Decline</OutlineBtn>
                    </div>
                  )
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {docs.length === 0 ? (
        <Card style={{ padding: 32, textAlign: 'center' }}>
          <FileText size={32} style={{ color: AG.n300, marginBottom: 8 }} />
          <p style={{ color: AG.textSec, fontSize: 14 }}>No documents yet</p>
          <p style={{ color: AG.n400, fontSize: 12, marginTop: 4 }}>Your HR team will upload your documents here.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {docs.map((d) => (
            <Card key={d.id} hover style={{ padding: 16 }} className="flex items-center gap-3">
              <div style={{ width: 44, height: 44, borderRadius: 12, background: AG.primaryLightest, color: AG.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <FileText size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="truncate" style={{ fontWeight: 500, fontSize: 14, color: AG.text }}>{d.name}{d.version > 1 ? ` · v${d.version}` : ''}</p>
                <p style={{ fontSize: 12, color: AG.n400 }}>{d.type} · {d.date}{d.size ? ` · ${fmtSize(d.size)}` : ''}</p>
                {d.expiresAt && (
                  <span style={{ fontSize: 11, fontWeight: 600, marginTop: 4, display: 'inline-block', borderRadius: 6, padding: '2px 6px', color: d.expiresAt < todayStr ? AG.errorDark : AG.warningDark, background: d.expiresAt < todayStr ? 'rgba(240,68,56,.12)' : 'rgba(247,144,9,.12)' }}>
                    {d.expiresAt < todayStr ? 'Expired' : 'Expires'} {d.expiresAt}
                  </span>
                )}
              </div>
              <button onClick={() => handleDownload(d)} title="Download" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: AG.n400, display: 'flex' }}>
                <Download size={16} />
              </button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- OKR / performance shared helpers ----

// ============ Employee: my OKRs (complete + self-rate + submit) ============
function EmpPerformance({ showToast }) {
  const [okrs, setOkrs] = useState(null);
  const [appeal, setAppeal] = useState(null); // { okr, reason }
  const [appealing, setAppealing] = useState(false);
  useEffect(() => {
    let a = true;
    api.okrsMe().then((d) => a && setOkrs(d)).catch(() => a && setOkrs([]));
    return () => { a = false; };
  }, []);
  const patchLocal = (id, patch) => setOkrs((prev) => prev.map((o) => (o.id === id ? { ...o, ...patch } : o)));
  const saveProgress = (o, progress) => { patchLocal(o.id, { progress }); api.updateOkrProgress(o.id, { progress }).catch(() => {}); };
  const saveSelf = (o, selfRating) => { patchLocal(o.id, { selfRating }); api.updateOkrProgress(o.id, { selfRating }).catch(() => {}); };
  const accept = (o) => { patchLocal(o.id, { status: 'Accepted' }); api.acceptOkr(o.id).then((u) => patchLocal(o.id, u)).catch(() => {}); };
  const submitAppeal = async () => {
    if (!appeal?.reason.trim() || appealing) return; setAppealing(true);
    try { const u = await api.objectOkr(appeal.okr.id, appeal.reason.trim()); patchLocal(appeal.okr.id, u); showToast?.('Appeal submitted'); setAppeal(null); }
    catch (e) { showToast?.(e.message); } finally { setAppealing(false); }
  };
  const submit = (o) => api.submitOkr(o.id, { selfRating: o.selfRating, progress: o.progress }).then((u) => patchLocal(o.id, u)).catch((e) => showToast?.(e.message));
  // Proof-of-work documents (required before submitting).
  const uploadEvidence = async (o, fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    try {
      const { uploaded, errors } = await api.uploadOkrEvidence(o.id, files);
      if (uploaded?.length) setOkrs((prev) => prev.map((x) => (x.id === o.id ? { ...x, evidence: [...(x.evidence || []), ...uploaded] } : x)));
      if (errors?.length) showToast?.(errors[0].error || 'Some files were rejected');
    } catch (e) { showToast?.(e.message); }
  };
  const removeEvidence = async (o, evId) => {
    try {
      await api.deleteOkrEvidence(o.id, evId);
      setOkrs((prev) => prev.map((x) => (x.id === o.id ? { ...x, evidence: (x.evidence || []).filter((e) => e.id !== evId) } : x)));
    } catch (e) { showToast?.(e.message); }
  };
  const downloadEvidence = (o, ev) => api.downloadOkrEvidence(o.id, ev.id, ev.name).catch((e) => showToast?.(e.message));

  if (!okrs) return <div className="flex items-center justify-center" style={{ minHeight: 220 }}><CenterSpinner /></div>;
  const overall = weightedProgress(okrs);
  const pending = okrs.filter((o) => o.status === 'Submitted').length;
  const reviewed = okrs.filter((o) => o.status === 'Reviewed').length;

  return (
    <div className="space-y-4 fade-up">
      <SectionTitle title="Performance" sub="Your OKRs · H1 2026 cycle" />
      <WidgetGrid>
        <KPI label="Overall progress" value={`${overall}%`} icon={TrendingUp} iconTone="primary" delay={0} />
        <KPI label="Objectives" value={String(okrs.length)} icon={Star} iconTone="info" delay={60} />
        <KPI label="Awaiting review" value={String(pending)} icon={Clock} iconTone="warning" delay={120} />
        <KPI label="Reviewed" value={String(reviewed)} icon={Check} iconTone="success" delay={180} />
      </WidgetGrid>
      {okrs.length === 0 ? (
        <Card><EmptyState icon={Star} title="No objectives yet" desc="Your manager hasn't assigned any OKRs for this cycle." /></Card>
      ) : (
        okrs.map((o) => {
          const locked = o.status === 'Reviewed' || o.status === 'Finalized' || o.status === 'Objected';
          const pending = o.status === 'Assigned'; // not yet accepted by the employee
          return (
            <Card key={o.id} style={{ padding: 20 }}>
              <div className="flex items-center gap-2 flex-wrap">
                <p style={{ fontWeight: 600, color: AG.text, fontSize: 15 }}>{o.objective}</p>
                <Pill tone={okrStatusTone(o.status)} dot>{o.status}</Pill>
                <Pill tone="neutral">Weight {o.weight}%</Pill>
                {o.cycle && <Pill tone="info">{o.cycle}</Pill>}
              </div>
              {o.alignedTo && <p style={{ fontSize: 12, color: AG.n500, marginTop: 4 }}>↳ Aligns to: <b style={{ color: AG.n600 }}>{o.alignedTo}</b></p>}
              {(o.metric || o.target) && <p style={{ fontSize: 13, color: AG.n500, marginTop: 4 }}>{o.metric}{o.target ? ` · Target: ${o.target}` : ''}</p>}
              {o.description && <p style={{ fontSize: 13, color: AG.n500, marginTop: 4 }}>{o.description}</p>}

              {pending ? (
                <div style={{ marginTop: 14, padding: 14, background: AG.n50, borderRadius: 12 }} className="flex items-center gap-3 flex-wrap">
                  <span style={{ fontSize: 13, color: AG.n600 }}>Your manager assigned this objective. Accept it to start tracking progress.</span>
                  <button onClick={() => accept(o)} className="ag-btn ag-btn-primary" style={{ marginLeft: 'auto' }}>Accept objective</button>
                </div>
              ) : (
                <>
                  <div style={{ marginTop: 14 }}>
                    <div className="flex items-center justify-between" style={{ marginBottom: 6 }}>
                      <span style={{ fontSize: 13, color: AG.n500 }}>Progress</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: AG.text }}>{o.progress}%</span>
                    </div>
                    <input type="range" min="0" max="100" value={o.progress} disabled={locked}
                      onChange={(e) => patchLocal(o.id, { progress: Number(e.target.value) })}
                      onMouseUp={(e) => saveProgress(o, Number(e.target.value))}
                      onTouchEnd={(e) => saveProgress(o, Number(e.target.value))}
                      style={{ width: '100%', accentColor: AG.primary }} />
                  </div>
                  <div className="flex items-center gap-3 flex-wrap" style={{ marginTop: 14 }}>
                    <label style={{ fontSize: 13, color: AG.n500 }}>Self-rating</label>
                    <select className="ag-input" style={{ width: 'auto' }} value={o.selfRating || ''} disabled={locked} onChange={(e) => saveSelf(o, e.target.value)}>
                      <option value="">Select…</option>
                      {OKR_RATINGS.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                  {!locked && (
                    <div style={{ marginTop: 14 }}>
                      <div className="flex items-center justify-between" style={{ marginBottom: 6 }}>
                        <span style={{ fontSize: 13, color: AG.n500 }}>Proof of work <span style={{ color: AG.error }}>*</span></span>
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: AG.primary, border: `1px solid ${AG.n200}`, borderRadius: 8, padding: '4px 10px' }}>
                          <Plus size={14} /> Attach file
                          <input type="file" multiple hidden onChange={(e) => { uploadEvidence(o, e.target.files); e.target.value = ''; }} />
                        </label>
                      </div>
                      {o.evidence?.length ? (
                        <div className="space-y-1">
                          {o.evidence.map((ev) => (
                            <div key={ev.id} className="flex items-center gap-2" style={{ padding: '6px 10px', background: AG.n50, borderRadius: 8 }}>
                              <FileText size={14} style={{ color: AG.n400, flexShrink: 0 }} />
                              <button onClick={() => downloadEvidence(o, ev)} className="truncate" style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.primary, fontSize: 13, flex: 1, textAlign: 'left', minWidth: 0 }}>{ev.name}</button>
                              <button onClick={() => removeEvidence(o, ev.id)} title="Remove" style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.n400, display: 'flex', flexShrink: 0 }}><X size={14} /></button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p style={{ fontSize: 12, color: AG.n400 }}>Attach at least one document proving your work before submitting.</p>
                      )}
                    </div>
                  )}
                  {!locked && (
                    <div className="flex items-center gap-3 flex-wrap" style={{ marginTop: 14 }}>
                      <button onClick={() => submit(o)} disabled={!o.selfRating || !o.evidence?.length} className="ag-btn ag-btn-primary" style={{ marginLeft: 'auto' }}>{o.status === 'Submitted' ? 'Re-submit' : 'Submit for review'}</button>
                    </div>
                  )}
                </>
              )}
              {o.status === 'Submitted' && <p style={{ fontSize: 12, color: AG.warningDark, marginTop: 10 }}>Submitted — awaiting your manager's review.</p>}
              {locked && (
                <div style={{ marginTop: 14, padding: 14, background: AG.n50, borderRadius: 12 }}>
                  <div className="flex items-center gap-2"><span style={{ fontSize: 13, color: AG.n500 }}>Manager rating</span><Pill tone={ratingTone(o.managerRating)} dot>{o.managerRating}</Pill></div>
                  {o.managerComment && <p style={{ fontSize: 13, color: AG.n600, marginTop: 6 }}>“{o.managerComment}”</p>}
                  {o.status === 'Finalized' && (
                    <div className="flex items-center gap-2" style={{ marginTop: 10 }}>
                      <Check size={14} color={AG.primary} />
                      <span style={{ fontSize: 12, color: AG.primaryDark, fontWeight: 600 }}>Signed off by HR</span>
                      {o.hrComment && <span style={{ fontSize: 13, color: AG.n600 }}>— “{o.hrComment}”</span>}
                    </div>
                  )}
                  {/* Appeal (Phase 1.2): open within the objection window, then under review, then resolved. */}
                  {o.objectionOutcome ? (
                    <div className="flex items-center gap-2" style={{ marginTop: 10 }}>
                      <Pill tone={o.objectionOutcome === 'Upheld' ? 'success' : 'neutral'} dot>Appeal {o.objectionOutcome.toLowerCase()}</Pill>
                      {o.objectionReason && <span style={{ fontSize: 12.5, color: AG.n500 }}>— “{o.objectionReason}”</span>}
                    </div>
                  ) : o.status === 'Objected' ? (
                    <div className="flex items-center gap-2" style={{ marginTop: 10 }}>
                      <Pill tone="warning" dot>Appeal under review</Pill>
                      {o.objectionReason && <span style={{ fontSize: 12.5, color: AG.n500 }}>— “{o.objectionReason}”</span>}
                    </div>
                  ) : o.appealOpen ? (
                    <div className="flex items-center gap-3 flex-wrap" style={{ marginTop: 10 }}>
                      <span style={{ fontSize: 12.5, color: AG.n500 }}>Disagree with this review? You can appeal while the window is open.</span>
                      <button onClick={() => setAppeal({ okr: o, reason: '' })} className="ag-btn ag-btn-outline" style={{ marginLeft: 'auto' }}>Appeal rating</button>
                    </div>
                  ) : null}
                </div>
              )}
            </Card>
          );
        })
      )}
      <Dialog open={!!appeal} onClose={() => !appealing && setAppeal(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Appeal review</DialogTitle>
        <DialogContent>
          {appeal && (
            <div style={{ paddingTop: 6 }}>
              <p style={{ fontSize: 13, color: AG.n500, marginBottom: 10 }}>Appealing “{appeal.okr.objective}” (rated {appeal.okr.managerRating}). HR will review your grounds and either uphold or reject the appeal.</p>
              <TextField fullWidth size="small" multiline minRows={3} label="Grounds for appeal" value={appeal.reason} onChange={(e) => setAppeal({ ...appeal, reason: e.target.value })} placeholder="Explain why you believe the rating should be reconsidered…" />
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <OutlineBtn onClick={() => setAppeal(null)} disabled={appealing}>Cancel</OutlineBtn>
          <PrimaryBtn onClick={submitAppeal} disabled={appealing || !appeal?.reason.trim()}>Submit appeal</PrimaryBtn>
        </DialogActions>
      </Dialog>
    </div>
  );
}

// ============ Manager: assign OKRs, track team, review submissions ============
function TeamPerformance({ showToast }) {
  const [okrs, setOkrs] = useState(null);
  const [tab, setTab] = useState('Pending review');
  const [assignOpen, setAssignOpen] = useState(false);
  useEffect(() => {
    let a = true;
    api.okrsTeam().then((d) => a && setOkrs(d)).catch(() => a && setOkrs([]));
    return () => { a = false; };
  }, []);
  if (!okrs) return <div className="flex items-center justify-center" style={{ minHeight: 220 }}><CenterSpinner /></div>;

  const reports = Object.values(okrs.reduce((m, o) => { m[o.employeeId] = { id: o.employeeId, name: o.employeeName }; return m; }, {}));
  const pending = okrs.filter((o) => o.status === 'Submitted');
  const reviewedCount = okrs.filter((o) => o.status === 'Reviewed').length;

  const review = async (o, managerRating, managerComment) => {
    try { const u = await api.reviewOkr(o.id, { managerRating, managerComment }); setOkrs((prev) => prev.map((x) => (x.id === o.id ? u : x))); showToast?.(`Reviewed ${o.employeeName}'s objective`); }
    catch (e) { showToast?.(e.message); }
  };
  const assign = async (data) => {
    const created = await api.createOkr(data);
    setOkrs((prev) => [...prev, created]);
    showToast?.(`OKR assigned to ${created.employeeName}`);
    setAssignOpen(false);
  };
  const tabs = [['Pending review', pending.length], ['Team OKRs', okrs.length]];

  return (
    <div className="space-y-4 fade-up">
      <SectionTitle title="Team's Performance" sub="Assign OKRs, track progress and review your team" action={<PrimaryBtn icon={Plus} onClick={() => setAssignOpen(true)}>Assign OKR</PrimaryBtn>} />
      <WidgetGrid>
        <KPI label="Team objectives" value={String(okrs.length)} icon={Star} iconTone="primary" delay={0} />
        <KPI label="Awaiting review" value={String(pending.length)} icon={Clock} iconTone="warning" delay={60} />
        <KPI label="Reviewed" value={String(reviewedCount)} icon={Check} iconTone="success" delay={120} />
        <KPI label="Team members" value={String(reports.length)} icon={Users} iconTone="info" delay={180} />
      </WidgetGrid>
      <div className="flex gap-2 flex-wrap">
        {tabs.map(([t, n]) => <button key={t} onClick={() => setTab(t)} className="navlift" style={okrTabBtn(tab === t)}>{t} <span style={{ opacity: 0.7 }}>({n})</span></button>)}
      </div>

      {tab === 'Pending review' && (
        pending.length === 0
          ? <Card><EmptyState icon={Check} title="Nothing to review" desc="No objectives are awaiting your review right now." /></Card>
          : <div className="space-y-3">{pending.map((o) => <OkrReviewCard key={o.id} o={o} onReview={review} />)}</div>
      )}

      {tab === 'Team OKRs' && (
        <div className="space-y-4">
          {reports.map((r) => {
            const list = okrs.filter((o) => o.employeeId === r.id);
            return (
              <Card key={r.id} style={{ padding: 18 }}>
                <div className="flex items-center gap-3" style={{ marginBottom: 12 }}>
                  <PersonCell name={r.name} />
                  <span style={{ marginLeft: 'auto', fontSize: 13, color: AG.n500 }}>Overall <b style={{ color: AG.text }}>{weightedProgress(list)}%</b></span>
                </div>
                <div className="space-y-2">
                  {list.map((o) => (
                    <div key={o.id} className="flex items-center gap-3 flex-wrap" style={{ padding: 10, background: AG.n50, borderRadius: 10 }}>
                      <div style={{ flex: 1, minWidth: 180 }}>
                        <div className="flex items-center gap-2 flex-wrap"><span style={{ fontSize: 14, fontWeight: 500, color: AG.text }}>{o.objective}</span><Pill tone="neutral">W {o.weight}%</Pill></div>
                      </div>
                      <div style={{ width: 90, height: 8, background: AG.n100, borderRadius: 4, overflow: 'hidden' }}><div style={{ height: '100%', background: AG.primary, width: `${o.progress}%` }} /></div>
                      <span style={{ fontSize: 12, width: 36, textAlign: 'right', color: AG.n600 }}>{o.progress}%</span>
                      <Pill tone={okrStatusTone(o.status)} dot>{o.status}</Pill>
                      {o.managerRating && <Pill tone={ratingTone(o.managerRating)}>{o.managerRating}</Pill>}
                    </div>
                  ))}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {assignOpen && <AssignOkrModal reports={reports} existing={okrs} onClose={() => setAssignOpen(false)} onSubmit={assign} />}
    </div>
  );
}

function OkrReviewCard({ o, onReview }) {
  const [rating, setRating] = useState('');
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const go = async () => { setBusy(true); await onReview(o, rating, comment); setBusy(false); };
  return (
    <Card style={{ padding: 18 }}>
      <div className="flex items-center gap-3 flex-wrap" style={{ marginBottom: 10 }}>
        <PersonCell name={o.employeeName} />
        <Pill tone="neutral">{o.dept}</Pill>
        <span style={{ marginLeft: 'auto', fontSize: 13, color: AG.n500 }}>Self-rating: <b style={{ color: AG.text }}>{o.selfRating || '—'}</b></span>
      </div>
      <p style={{ fontWeight: 600, color: AG.text }}>{o.objective}</p>
      {(o.metric || o.target) && <p style={{ fontSize: 13, color: AG.n500, marginTop: 2 }}>{o.metric}{o.target ? ` · Target: ${o.target}` : ''}</p>}
      {o.evidence?.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: AG.n500 }}>Proof of work</span>
          <div className="space-y-1" style={{ marginTop: 4 }}>
            {o.evidence.map((ev) => (
              <button key={ev.id} onClick={() => api.downloadOkrEvidence(o.id, ev.id, ev.name)} className="flex items-center gap-2" style={{ width: '100%', background: AG.n50, border: 'none', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', textAlign: 'left' }}>
                <FileText size={14} style={{ color: AG.n400, flexShrink: 0 }} />
                <span className="truncate" style={{ fontSize: 13, color: AG.primary, flex: 1, minWidth: 0 }}>{ev.name}</span>
                <Download size={14} style={{ color: AG.n400, flexShrink: 0 }} />
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="flex items-center gap-2" style={{ marginTop: 10 }}>
        <div style={{ flex: 1, height: 8, background: AG.n100, borderRadius: 4, overflow: 'hidden' }}><div style={{ height: '100%', background: AG.primary, width: `${o.progress}%` }} /></div>
        <span style={{ fontSize: 12, color: AG.n600 }}>{o.progress}%</span>
      </div>
      <div className="flex items-center gap-2 flex-wrap" style={{ marginTop: 14 }}>
        <select className="ag-input" style={{ width: 'auto' }} value={rating} onChange={(e) => setRating(e.target.value)}>
          <option value="">Manager rating…</option>
          {OKR_RATINGS.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <input className="ag-input" style={{ flex: 1, minWidth: 180 }} placeholder="Comment (optional)" value={comment} onChange={(e) => setComment(e.target.value)} />
        <button className="ag-btn ag-btn-primary" disabled={!rating || busy} onClick={go}>{busy ? 'Saving…' : 'Submit review'}</button>
      </div>
    </Card>
  );
}

function AssignOkrModal({ reports, existing = [], onClose, onSubmit }) {
  const [f, setF] = useState({ employeeId: reports[0]?.id || '', objective: '', description: '', metric: '', target: '', weight: '25', dueDate: '', cycleType: 'Quarterly', cycle: cycleOptionsFor('Quarterly')[0], cycleId: '', parentId: '' });
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  // Changing the period type re-bases the available cycle options.
  const setCycleType = (e) => { const t = e.target.value; setF((s) => ({ ...s, cycleType: t, cycle: cycleOptionsFor(t)[0] })); };
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // Optional managed performance cycle (open) — when chosen it supplies the cycle
  // label + gates assignment server-side.
  const [mCycles, setMCycles] = useState([]);
  useEffect(() => { api.openPerformanceCycles().then(setMCycles).catch(() => setMCycles([])); }, []);
  const setManagedCycle = (e) => {
    const id = e.target.value;
    const picked = mCycles.find((c) => c.id === id);
    setF((s) => ({ ...s, cycleId: id, cycle: picked ? picked.name : cycleOptionsFor(s.cycleType)[0] }));
  };

  // Weightage guardrail: an employee's objectives in one cycle should total 100%.
  const cycleOpts = cycleOptionsFor(f.cycleType);
  const usedWeight = existing
    .filter((o) => o.employeeId === f.employeeId && o.cycle === f.cycle)
    .reduce((a, o) => a + (o.weight || 0), 0);
  const addWeight = Number(f.weight) || 0;
  const newTotal = usedWeight + addWeight;
  const over = newTotal > 100;

  const valid = f.employeeId && f.objective.trim() && f.cycle && !over;
  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true); setErr('');
    try { await onSubmit({ ...f, weight: addWeight }); }
    catch (e) { setErr(e.message || 'Could not assign'); setBusy(false); }
  };
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <Box sx={{ p: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>Assign an OKR</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Set the objective, key result, period and weightage for a team member.</Typography>
        <Stack spacing={1.5}>
          <div><label style={okrLbl}>Employee</label><select className="ag-input" value={f.employeeId} onChange={set('employeeId')}>{reports.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</select></div>
          <div className="grid grid-cols-2 gap-2">
            <div><label style={okrLbl}>Period type</label><select className="ag-input" value={f.cycleType} onChange={setCycleType}>{CYCLE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</select></div>
            <div><label style={okrLbl}>Cycle</label><select className="ag-input" value={f.cycle} onChange={set('cycle')}>{cycleOpts.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
          </div>
          {mCycles.length > 0 && (
            <div><label style={okrLbl}>Performance cycle (optional)</label><select className="ag-input" value={f.cycleId} onChange={setManagedCycle}><option value="">— Use period above —</option>{mCycles.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
          )}
          {existing.length > 0 && (
            <div><label style={okrLbl}>Aligns to (optional)</label><select className="ag-input" value={f.parentId} onChange={set('parentId')}><option value="">— None —</option>{existing.map((o) => <option key={o.id} value={o.id}>{o.objective}{o.employeeName ? ` · ${o.employeeName}` : ''}</option>)}</select></div>
          )}
          <div><label style={okrLbl}>Objective</label><input className="ag-input" value={f.objective} onChange={set('objective')} placeholder="e.g. Improve onboarding experience" /></div>
          <div><label style={okrLbl}>Description</label><input className="ag-input" value={f.description} onChange={set('description')} placeholder="Optional context" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><label style={okrLbl}>Key result / KPI</label><input className="ag-input" value={f.metric} onChange={set('metric')} placeholder="e.g. NPS score" /></div>
            <div><label style={okrLbl}>Target</label><input className="ag-input" value={f.target} onChange={set('target')} placeholder="e.g. 60" /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><label style={okrLbl}>Weight (%)</label><input type="number" min="0" max="100" className="ag-input" value={f.weight} onChange={set('weight')} /></div>
            <div><label style={okrLbl}>Due date</label><input type="date" className="ag-input" value={f.dueDate} onChange={set('dueDate')} /></div>
          </div>
          <Box sx={{ fontSize: 12.5, color: over ? 'error.main' : newTotal === 100 ? 'success.main' : 'text.secondary', bgcolor: AG.n50, borderRadius: 2, p: 1 }}>
            Allocated for <b>{f.cycle}</b>: {usedWeight}% already + {addWeight}% = <b>{newTotal}%</b>
            {over ? ' — exceeds 100%, reduce the weight.' : newTotal === 100 ? ' — fully allocated ✓' : ' (target 100%).'}
          </Box>
          {err && <Box sx={{ bgcolor: 'error.light', color: 'error.dark', fontSize: 13, p: 1, borderRadius: 2 }}>{err}</Box>}
        </Stack>
        <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 3 }}>
          <Button variant="text" color="inherit" onClick={onClose}>Cancel</Button>
          <Button variant="contained" color="primary" disableElevation disabled={!valid || busy} onClick={submit}>{busy ? 'Assigning…' : 'Assign OKR'}</Button>
        </Stack>
      </Box>
    </Dialog>
  );
}

function EmpPolicies({ showToast }) {
  const [policies, setPolicies] = useState([]);
  useEffect(() => {
    let a = true;
    api
      .policies()
      // Policies tab shows only policies — forms live under Organization Docs.
      .then((d) => a && setPolicies(d.filter((p) => (p.kind || 'Policy') === 'Policy')))
      .catch(() => {});
    return () => {
      a = false;
    };
  }, []);
  return (
    <div className="space-y-4 fade-up">
      <SectionTitle
        title="Company policies"
        sub="Review company policies"
      />
      {policies.length === 0 ? (
        <Card>
          <EmptyState
            icon={FileText}
            title="No policies yet"
            desc="Company policies haven't been published yet. Check back later."
          />
        </Card>
      ) : (
      <div className="space-y-2">
        {policies.map((p) => (
          <Card
            key={p.id}
            style={{ padding: 16 }}
            className="flex items-center gap-4"
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                background: AG.n100,
                color: AG.n500,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <FileText size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p style={{ fontWeight: 600, color: AG.text }}>{p.name}</p>
                <Pill tone="neutral">{p.cat}</Pill>
              </div>
              <p style={{ fontSize: 12, color: AG.n400, marginTop: 2 }}>
                Updated {p.updated}
              </p>
            </div>
            <button
              onClick={() => p.hasFile && api.downloadPolicy(p.id, p.name).catch(() => showToast?.('Download failed'))}
              disabled={!p.hasFile}
              title={p.hasFile ? 'Download' : 'No file attached'}
              className="inline-flex items-center gap-1"
              style={{
                flexShrink: 0,
                color: p.hasFile ? AG.primary : AG.n300,
                fontWeight: 500,
                fontSize: 13,
                background: 'none',
                border: 'none',
                cursor: p.hasFile ? 'pointer' : 'default',
                padding: 4,
              }}
            >
              <Download size={15} /> PDF
            </button>
          </Card>
        ))}
      </div>
      )}
    </div>
  );
}


export { EmployeeViews };
